/**
 * Package Service - React Query Integration
 * Handles all package-related API communications
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';
import {
  Package,
  PackageCapabilityAssignment,
  PackageCapabilityAssignmentInput,
  PackageFeatureCatalogItem,
  PackageToolAssignment,
  PackageToolAssignmentInput,
  UpdatePackageStatusInput,
} from '../types/admin';
import { AxiosError } from 'axios';

// ============================================
// Input Types
// ============================================

export interface CreatePackageInput {
  code: string;
  name: string;
  descriptionEn?: string | null;
  descriptionVi?: string | null;
  highlightsEn?: string | null;
  highlightsVi?: string | null;
  price: number;
  priceVnd?: number;
  maxAgents: number;
  tokensPerMonth: number;
  advanceTokensPerMonth: number;
  duration: number;
  isActive: boolean;
  allowDynamicTheme?: boolean;
  features?: string[];
  featureLimits?: Record<string, number | null>;
  featureDailyLimits?: Record<string, number | null>;
  integrationEntitlements?: string[];
  integrationLimits?: Record<string, number>;
}

export interface UpdatePackageInput {
  publicId: string;
  name?: string;
  descriptionEn?: string | null;
  descriptionVi?: string | null;
  highlightsEn?: string | null;
  highlightsVi?: string | null;
  price?: number;
  priceVnd?: number;
  maxAgents?: number;
  tokensPerMonth?: number;
  advanceTokensPerMonth?: number;
  duration?: number;
  isActive?: boolean;
  allowDynamicTheme?: boolean;
  features?: string[];
  featureLimits?: Record<string, number | null>;
  featureDailyLimits?: Record<string, number | null>;
  integrationEntitlements?: string[];
  integrationLimits?: Record<string, number>;
}

// ============================================
// API Response Types
// ============================================

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

type BackendPackage = Omit<Package, 'price'> & {
  price?: number;
  prices?: Array<{
    currency: string;
    amountMinor: string | number;
    billingPeriod: string;
    isActive: boolean;
  }>;
};

const toPackageRequest = <T extends CreatePackageInput | Omit<UpdatePackageInput, 'publicId'>>(
  data: T
) => {
  const prices: Array<{
    currency: string;
    amountMinor: number;
    billingPeriod: string;
    isActive: boolean;
  }> = [];

  if (data.price !== undefined) {
    prices.push({
      currency: 'USD',
      amountMinor: Math.round(data.price * 100),
      billingPeriod: 'month',
      isActive: true,
    });
  }

  if (data.priceVnd !== undefined && data.priceVnd !== null) {
    prices.push({
      currency: 'VND',
      amountMinor: Math.round(data.priceVnd),
      billingPeriod: 'month',
      isActive: true,
    });
  }

  const rest = { ...(data as Record<string, unknown>) };
  const features = Array.isArray(data.features) ? data.features : undefined;
  const nullableTextFields = ['descriptionEn', 'descriptionVi', 'highlightsEn', 'highlightsVi'];
  delete rest.priceVnd;

  nullableTextFields.forEach((field) => {
    if (field in rest) {
      const value = rest[field];
      rest[field] = typeof value === 'string' && value.trim() === '' ? null : (value ?? null);
    }
  });

  return {
    ...rest,
    features,
    prices: prices.length > 0 ? prices : undefined,
  };
};

const normalizePackage = (pkg: BackendPackage): Package => {
  const usdPrice = pkg.prices?.find((price) => price.currency === 'USD');
  const vndPrice = pkg.prices?.find((price) => price.currency === 'VND');
  const amountMinorUsd =
    usdPrice?.amountMinor === undefined ? undefined : Number(usdPrice.amountMinor);
  const amountMinorVnd =
    vndPrice?.amountMinor === undefined ? undefined : Number(vndPrice.amountMinor);

  return {
    ...pkg,
    price:
      typeof pkg.price === 'number'
        ? pkg.price
        : Number.isFinite(amountMinorUsd)
          ? (amountMinorUsd as number) / 100
          : 0,
    priceVnd: Number.isFinite(amountMinorVnd) ? (amountMinorVnd as number) : undefined,
    prices: pkg.prices,
    advanceTokensPerMonth: pkg.advanceTokensPerMonth ?? 0,
    allowDynamicTheme: pkg.allowDynamicTheme === true,
    features: Array.isArray(pkg.features) ? pkg.features : [],
    featureLimits: pkg.featureLimits ?? {},
    featureDailyLimits: pkg.featureDailyLimits ?? {},
    descriptionEn: pkg.descriptionEn ?? null,
    descriptionVi: pkg.descriptionVi ?? null,
    highlightsEn: pkg.highlightsEn ?? null,
    highlightsVi: pkg.highlightsVi ?? null,
  };
};

// ============================================
// Query Keys
// ============================================

export const packageKeys = {
  all: ['packages'] as const,
  lists: () => [...packageKeys.all, 'list'] as const,
  list: (filters: string) => [...packageKeys.lists(), { filters }] as const,
  details: () => [...packageKeys.all, 'detail'] as const,
  detail: (id: string) => [...packageKeys.details(), id] as const,
  capabilities: (publicId: string) => [...packageKeys.detail(publicId), 'capabilities'] as const,
  tools: (publicId: string) => [...packageKeys.detail(publicId), 'tools'] as const,
  featureCatalog: () => [...packageKeys.all, 'featureCatalog'] as const,
};

// ============================================
// API Functions
// ============================================

/**
 * Fetches all packages from the backend
 */
const fetchPackages = async (): Promise<Package[]> => {
  try {
    const response = await axiosInstance.get<ApiResponse<BackendPackage[]>>('/v1/admin/packages');
    return (response.data.data || []).map(normalizePackage);
  } catch (error) {
    console.error('Error fetching packages:', error);
    throw error;
  }
};

const fetchPackageFeatureCatalog = async (): Promise<PackageFeatureCatalogItem[]> => {
  try {
    const response = await axiosInstance.get<ApiResponse<PackageFeatureCatalogItem[]>>(
      '/v1/admin/packages/catalog/features'
    );
    return response.data.data || [];
  } catch (error) {
    console.error('Error fetching package feature catalog:', error);
    throw error;
  }
};

/**
 * Creates a new package
 */
const createPackage = async (data: CreatePackageInput): Promise<Package> => {
  try {
    const response = await axiosInstance.post<ApiResponse<BackendPackage>>(
      '/v1/admin/packages',
      toPackageRequest(data)
    );
    return normalizePackage(response.data.data as BackendPackage);
  } catch (error) {
    console.error('Error creating package:', error);
    throw error;
  }
};

/**
 * Updates an existing package
 */
const updatePackage = async ({ publicId, ...data }: UpdatePackageInput): Promise<Package> => {
  try {
    const response = await axiosInstance.patch<ApiResponse<Package>>(
      `/v1/admin/packages/${publicId}`,
      toPackageRequest(data)
    );
    return normalizePackage(response.data.data as BackendPackage);
  } catch (error) {
    console.error('Error updating package:', error);
    throw error;
  }
};

/**
 * Updates package active status
 */
const updatePackageStatus = async ({
  publicId,
  isActive,
}: UpdatePackageStatusInput): Promise<Package> => {
  try {
    const response = await axiosInstance.patch<ApiResponse<BackendPackage>>(
      `/v1/admin/packages/${publicId}/status`,
      { isActive }
    );
    return normalizePackage(response.data.data as BackendPackage);
  } catch (error) {
    console.error('Error updating package status:', error);
    throw error;
  }
};

/**
 * Fetches PackageCapability assignments for a package
 */
const fetchPackageCapabilities = async (
  publicId: string
): Promise<PackageCapabilityAssignment[]> => {
  try {
    const response = await axiosInstance.get<ApiResponse<PackageCapabilityAssignment[]>>(
      `/v1/admin/packages/${publicId}/capabilities`
    );
    return response.data.data || [];
  } catch (error) {
    console.error('Error fetching package capabilities:', error);
    throw error;
  }
};

/**
 * Replaces catalog-backed PackageCapability assignments for a package
 */
const updatePackageCapabilities = async ({
  publicId,
  capabilities,
}: {
  publicId: string;
  capabilities: PackageCapabilityAssignmentInput[];
}): Promise<PackageCapabilityAssignment[]> => {
  try {
    const response = await axiosInstance.put<ApiResponse<PackageCapabilityAssignment[]>>(
      `/v1/admin/packages/${publicId}/capabilities`,
      { capabilities }
    );
    return response.data.data || [];
  } catch (error) {
    console.error('Error updating package capabilities:', error);
    throw error;
  }
};

const fetchPackageTools = async (publicId: string): Promise<PackageToolAssignment[]> => {
  try {
    const response = await axiosInstance.get<ApiResponse<PackageToolAssignment[]>>(
      `/v1/admin/packages/${publicId}/tools`
    );
    return response.data.data || [];
  } catch (error) {
    console.error('Error fetching package tools:', error);
    throw error;
  }
};

const updatePackageTools = async ({
  publicId,
  tools,
}: {
  publicId: string;
  tools: PackageToolAssignmentInput[];
}): Promise<PackageToolAssignment[]> => {
  try {
    const response = await axiosInstance.put<ApiResponse<PackageToolAssignment[]>>(
      `/v1/admin/packages/${publicId}/tools`,
      { tools }
    );
    return response.data.data || [];
  } catch (error) {
    console.error('Error updating package tools:', error);
    throw error;
  }
};

// ============================================
// React Query Hooks
// ============================================

/**
 * Hook to fetch all packages
 */
export const usePackages = () => {
  return useQuery({
    queryKey: packageKeys.lists(),
    queryFn: fetchPackages,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (previously cacheTime)
  });
};

/**
 * Hook to fetch capability assignments for a package
 */
export const usePackageCapabilities = (publicId?: string) => {
  return useQuery({
    queryKey: packageKeys.capabilities(publicId || ''),
    queryFn: () => fetchPackageCapabilities(publicId as string),
    enabled: !!publicId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const usePackageTools = (publicId?: string) => {
  return useQuery({
    queryKey: packageKeys.tools(publicId || ''),
    queryFn: () => fetchPackageTools(publicId as string),
    enabled: !!publicId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const usePackageFeatureCatalog = () => {
  return useQuery({
    queryKey: packageKeys.featureCatalog(),
    queryFn: fetchPackageFeatureCatalog,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
};

/**
 * Hook to create a new package
 */
export const useCreatePackage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createPackage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: packageKeys.lists() });
    },
    onError: (error: AxiosError<ApiResponse>) => {
      console.error('Error creating package:', error);
      throw error;
    },
  });
};

/**
 * Hook to update an existing package
 */
export const useUpdatePackage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updatePackage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: packageKeys.lists() });
    },
    onError: (error: AxiosError<ApiResponse>) => {
      console.error('Error updating package:', error);
      throw error;
    },
  });
};

/**
 * Hook to toggle package active status with optimistic updates
 */
export const useTogglePackageStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updatePackageStatus,
    // Optimistic update
    onMutate: async (variables: UpdatePackageStatusInput) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: packageKeys.lists() });

      // Snapshot previous value
      const previousPackages = queryClient.getQueryData<Package[]>(packageKeys.lists());

      // Optimistically update
      if (previousPackages) {
        queryClient.setQueryData<Package[]>(
          packageKeys.lists(),
          previousPackages.map((pkg) =>
            pkg.publicId === variables.publicId ? { ...pkg, isActive: variables.isActive } : pkg
          )
        );
      }

      return { previousPackages };
    },
    // Rollback on error
    onError: (error: AxiosError, _variables, context) => {
      if (context?.previousPackages) {
        queryClient.setQueryData(packageKeys.lists(), context.previousPackages);
      }
      console.error('Error toggling package status:', error);
    },
    // Refetch on success
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: packageKeys.lists() });
    },
  });
};

/**
 * Hook to update package capability assignments
 */
export const useUpdatePackageCapabilities = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updatePackageCapabilities,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: packageKeys.capabilities(variables.publicId) });
      queryClient.invalidateQueries({ queryKey: packageKeys.lists() });
    },
    onError: (error: AxiosError<ApiResponse>) => {
      console.error('Error updating package capabilities:', error);
      throw error;
    },
  });
};

export const useUpdatePackageTools = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updatePackageTools,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: packageKeys.tools(variables.publicId) });
      queryClient.invalidateQueries({ queryKey: ['admin-tool-catalog'] });
    },
    onError: (error: AxiosError<ApiResponse>) => {
      console.error('Error updating package tools:', error);
      throw error;
    },
  });
};
